import React, { useState, useEffect } from "react";
import { useBuilding } from "../../context/BuildingContext";
import { reportsApi } from "../../services/api";
import Spinner from "../../components/common/Spinner";
import ErrorAlert from "../../components/common/ErrorAlert";
import { yearForPeriod, fmt } from "./reportUtils";

interface BudgetVsActualReportProps {
  period: string;
}

const BudgetVsActualReport: React.FC<BudgetVsActualReportProps> = ({
  period,
}) => {
  const { currentBuilding } = useBuilding();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<any>(null);

  useEffect(() => {
    if (!currentBuilding?.id) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const year = yearForPeriod(period);
        const data = await reportsApi.getBudgetVsActual(
          currentBuilding.id,
          year,
        );
        if (!cancelled) setReport(data);
      } catch (err) {
        console.error("Error fetching budget vs actual:", err);
        if (!cancelled)
          setError(
            "Failed to load budget vs actual report. Please try again later.",
          );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentBuilding?.id, period]);

  if (loading) {
    return <Spinner message="Loading budget vs actual report..." />;
  }

  if (error) {
    return <ErrorAlert message={error} />;
  }

  if (!report) {
    return <ErrorAlert message="No budget data available" />;
  }

  const getStatusColor = (status: string) => {
    if (status === "under") return "text-green-600";
    if (status === "over") return "text-red-600";
    return "text-yellow-600";
  };

  const getProgressBarColor = (status: string) => {
    if (status === "under") return "bg-green-600";
    if (status === "over") return "bg-red-600";
    return "bg-yellow-500";
  };

  return (
    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-2">
        Budget vs Actual
      </h2>
      <p className="text-sm text-gray-500 mb-6">Year {report.year}</p>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-3 mb-8">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <dt className="text-xs font-medium text-gray-500 uppercase">
              Total Budgeted
            </dt>
            <dd className="mt-2 text-2xl font-semibold text-gray-900">
              {fmt(report.totalBudgeted || 0)}
            </dd>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <dt className="text-xs font-medium text-gray-500 uppercase">
              Total Actual
            </dt>
            <dd className="mt-2 text-2xl font-semibold text-gray-900">
              {fmt(report.totalActual || 0)}
            </dd>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <dt className="text-xs font-medium text-gray-500 uppercase">
              Total Variance
            </dt>
            <dd
              className={`mt-2 text-2xl font-semibold ${
                (report.totalVariance || 0) >= 0
                  ? "text-green-600"
                  : "text-red-600"
              }`}
            >
              {fmt(Math.abs(report.totalVariance || 0))}
            </dd>
            <p
              className={`text-xs mt-1 ${
                (report.totalVariance || 0) >= 0
                  ? "text-green-600"
                  : "text-red-600"
              }`}
            >
              {(report.totalVariance || 0) >= 0
                ? "Under budget"
                : "Over budget"}
            </p>
          </div>
        </div>
      </div>

      {/* Overall Budget Use */}
      <div className="bg-white shadow rounded-lg mb-8 p-6">
        <p className="text-sm font-medium text-gray-600 mb-2">
          Overall Budget Utilization
        </p>
        <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
          <div
            className={`h-3 rounded-full ${
              report.overallPercentUsed > 100
                ? "bg-red-600"
                : report.overallPercentUsed > 90
                ? "bg-yellow-500"
                : "bg-green-600"
            }`}
            style={{ width: `${Math.min(report.overallPercentUsed, 100)}%` }}
          ></div>
        </div>
        <p className="text-sm text-gray-600">
          {report.overallPercentUsed?.toFixed(1) || 0}% of budget used
        </p>
      </div>

      {/* Category Breakdown Table */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
          <h3 className="text-lg font-medium text-gray-900">
            Category Breakdown
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Budgeted
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actual
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Variance
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  % Used
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {(report.categories || []).map((category: any) => (
                <tr key={category.category}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {category.category}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                    {fmt(category.budgetedAmount || 0)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                    {fmt(category.actualAmount || 0)}
                  </td>
                  <td
                    className={`px-6 py-4 whitespace-nowrap text-sm text-right font-medium ${
                      category.variance >= 0 ? "text-green-600" : "text-red-600"
                    }`}
                  >
                    {fmt(Math.abs(category.variance || 0))}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                    <div className="w-16 bg-gray-200 rounded-full h-2 mx-auto">
                      <div
                        className={`h-2 rounded-full ${getProgressBarColor(
                          category.status,
                        )}`}
                        style={{
                          width: `${Math.min(category.percentUsed, 100)}%`,
                        }}
                      ></div>
                    </div>
                    <p className="text-xs text-gray-600 mt-1">
                      {category.percentUsed?.toFixed(0) || 0}%
                    </p>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-block px-3 py-1 text-xs font-semibold rounded ${getStatusColor(
                        category.status,
                      )} bg-gray-50`}
                    >
                      {category.status === "under" && "✓ Under"}
                      {category.status === "over" && "⚠ Over"}
                      {category.status === "on-track" && "✓ On Track"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default BudgetVsActualReport;
